import { useEffect, useState } from 'react';
import { useBreakpoint } from '@buzzfeed/react-components';

const AD_TOOLBAR_SELECTOR = '.ad-toolbar';
const AD_AWARENESS_SELECTOR = '.Ad--awareness-bp';
const PHONE_MEDIA_QUERY = '(max-width: 767px)';

function measureToolbar() {
  const toolbar = document.querySelector(AD_TOOLBAR_SELECTOR);
  if (!toolbar) return 0;
  if (toolbar.classList.contains('close')) return 0;
  return toolbar.offsetHeight || 0;
}

function measureAwareness() {
  const ad = document.querySelector(AD_AWARENESS_SELECTOR);
  if (!ad) return 0;
  return ad.offsetHeight || 0;
}

/**
 * Sets --mobile-top-ad-height and --mobile-bottom-ad-height CSS variables
 * on document.documentElement based on measured ad element heights.
 * Only active on mobile viewports and when ads are not disabled.
 */
export default function useMobileAdHeightVars({ disableAds = false } = {}) {
  const isPhone = useBreakpoint(PHONE_MEDIA_QUERY);
  const [bottomHeight, setBottomHeight] = useState(0);
  const [topHeight, setTopHeight] = useState(0);

  useEffect(() => {
    if (disableAds || !isPhone || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      setBottomHeight(0);
      setTopHeight(0);
      return undefined;
    }

    function measure() {
      setBottomHeight(measureToolbar());
      setTopHeight(measureAwareness());
    }

    measure();

    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    });

    return () => observer.disconnect();
  }, [disableAds, isPhone]);

  useEffect(() => {
    if (disableAds || !isPhone) {
      document.documentElement.style.removeProperty('--mobile-top-ad-height');
      document.documentElement.style.removeProperty('--mobile-bottom-ad-height');
      return undefined;
    }

    document.documentElement.style.setProperty(
      '--mobile-top-ad-height',
      topHeight > 0 ? `${topHeight}px` : '0px',
    );
    document.documentElement.style.setProperty(
      '--mobile-bottom-ad-height',
      bottomHeight > 0 ? `${bottomHeight}px` : '0px',
    );

    return () => {
      document.documentElement.style.removeProperty('--mobile-top-ad-height');
      document.documentElement.style.removeProperty('--mobile-bottom-ad-height');
    };
  }, [disableAds, isPhone, topHeight, bottomHeight]);
}
